import React from "react";

import { faChartPieSimple, faMemoPad, faMoneyCheckDollar, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";

import { ButtonIcon, Tooltip } from "@performyard/py-components";
import type { ICellRendererParams } from "@performyard/py-components";

import type { DecisionRow } from "../../types";
import { DecisionTableContext } from "../MyDecisions/types";

import { appendCurrentLocationAsFrom } from "../../../../utils/compensationBreadcrumbs";

const NotesButton = ({ params, readOnly }: { params: ICellRendererParams<DecisionRow>; readOnly?: boolean }) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || row.superRow) return null;
	const notesExpanded = context?.expandedRowId === row._id;
	const noteExists = Boolean(row?.notes);
	const notes = row?.notes ?? null;
	const toggleNotesRow = () => {
		if (!context) return;
		if (context.expandedRowId === row._id) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};
	const placement = notes && notes.length >= 400 ? "left" : "top";
	if (readOnly) {
		return (
			<Tooltip content={notes} placement={placement} disabled={!notes}>
				<ButtonIcon
					icon={faMemoPad}
					color={noteExists ? "primary" : "neutral"}
					onClick={() => {}}
					aria-label={`Notes for ${row.employee_name}`}
					size={20}
					disabled={readOnly}
				/>
			</Tooltip>
		);
	}
	return (
		<Tooltip content={`Notes about ${row.employee_name}`} placement="top">
			<ButtonIcon
				icon={faMemoPad}
				color={noteExists ? "primary" : "neutral"}
				onClick={(e) => {
					e.stopPropagation();
					toggleNotesRow();
				}}
				aria-expanded={notesExpanded}
				aria-label={`${notesExpanded ? "Close" : "Open"} notes for ${row.employee_name}`}
				size={20}
			/>
		</Tooltip>
	);
};

const GoalsButton = ({ row, readOnly }: { row: DecisionRow; readOnly?: boolean }) => {
	if (readOnly) {
		return (
			<ButtonIcon
				color={"primary"}
				icon={faChartPieSimple}
				disabled={readOnly}
				href={appendCurrentLocationAsFrom(`/employees/${row.employee_id}/goals/`)}
				aria-label={`${row.employee_name}'s Goals`}
				size={20}
			/>
		);
	}
	return (
		<Tooltip content={`${row.employee_name}'s Goals`} placement="top">
			<ButtonIcon
				color={"primary"}
				icon={faChartPieSimple}
				href={appendCurrentLocationAsFrom(`/employees/${row.employee_id}/goals/`)}
				aria-label={`${row.employee_name}'s Goals`}
				size={20}
			/>
		</Tooltip>
	);
};

const ReviewsButton = ({ row, readOnly }: { row: DecisionRow; readOnly?: boolean }) => {
	if (readOnly) {
		return (
			<ButtonIcon
				color={"primary"}
				icon={faPenToSquare}
				disabled={readOnly}
				href={appendCurrentLocationAsFrom(`/reviews/${row.employee_id}/reviews-of-me/`)}
				aria-label={`${row.employee_name}'s Reviews`}
				size={20}
			/>
		);
	}
	return (
		<Tooltip content={`${row.employee_name}'s Reviews`} placement="top">
			<ButtonIcon
				color={"primary"}
				icon={faPenToSquare}
				href={appendCurrentLocationAsFrom(`/reviews/${row.employee_id}/reviews-of-me/`)}
				aria-label={`${row.employee_name}'s Reviews`}
				size={20}
			/>
		</Tooltip>
	);
};

const EmployeeSalaryHistoryButton = ({
	row,
	readOnly,
	salaryFieldId,
}: {
	row: DecisionRow;
	readOnly?: boolean;
	salaryFieldId?: string;
}) => {
	if (!salaryFieldId) return null;
	if (readOnly) {
		return (
			<ButtonIcon
				color={"primary"}
				icon={faMoneyCheckDollar}
				disabled={readOnly}
				href={`/view-field-history/${salaryFieldId}/${row.employee_id}`}
				aria-label={"View Salary History"}
				size={20}
			/>
		);
	}
	return (
		<Tooltip content={`${row.employee_name}'s Salary History`} placement="top">
			<ButtonIcon
				color={"primary"}
				icon={faMoneyCheckDollar}
				href={`/view-field-history/${salaryFieldId}/${row.employee_id}`}
				aria-label={"View Salary History"}
				size={20}
			/>
		</Tooltip>
	);
};

export const EmployeeInfoCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row || row.superRow) return null;
	const context = params.context as DecisionTableContext | undefined;

	return (
		<div className="flex h-full items-center gap-2">
			<NotesButton params={params} />
			<GoalsButton row={row} />
			<ReviewsButton row={row} />
			<EmployeeSalaryHistoryButton
				row={row}
				salaryFieldId={context?.salaryFieldId}
				readOnly={context?.managersExcludedFromSalaryHistory}
			/>
		</div>
	);
};

export const EmployeeInfoCellRendererBudgetReviews = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row || row.superRow) return null;

	const context = params.context as DecisionTableContext | undefined;
	const readOnly = context?.budgetReviewInfoLinksReadOnly;
	const canAlwaysViewSalary = context?.canAlwaysViewSalary;
	return (
		<div className="flex h-full items-center gap-3">
			<NotesButton params={params} readOnly={true} />
			<GoalsButton row={row} readOnly={readOnly} />
			<ReviewsButton row={row} readOnly={readOnly} />
			<EmployeeSalaryHistoryButton
				row={row}
				readOnly={!canAlwaysViewSalary}
				salaryFieldId={context?.salaryFieldId}
			/>
		</div>
	);
};
